import { useEffect, useState } from 'react';
import { ApiError, get, patch, post, put, type Me } from './api';
import { AvailNotice, DAY_FULL, MAX_D_OPTS, MAX_H_OPTS } from './AvailabilityModal';
import { PRIVACY_URL, SMS_TERMS, TERMS_URL } from './smsTerms';
import { TimeOffTab } from './TimeOff';
import { EmployeeMessages, useUnread } from './Messages';
import { Confirm, Modal, useToast, type ConfirmSpec } from './ui';
import { StoreTab } from './StoreTab';
import { CoverCards } from './Cover';
import { HelpPage, helpFor, startFor } from './HelpPage';

/**
 * The employee phone app (prototype "employee view"): Today, Week, Availability, Time off and Messages
 * (their POSTED shifts only).
 */

type Tab = 'today' | 'week' | 'avail' | 'timeoff' | 'messages' | 'store';

interface ShiftDay { date: string; label: string; blocks: string; working: boolean; shift: string; paidHours: number; breaks: string[]; store: string | null; updated: string | null; timeOff: string | null; left: string | null }
interface ShiftWeek { weekStart: string; label: string; weekEnd: string; hours: number; days: ShiftDay[] }
interface ShiftsResp { today: string; weeks: ShiftWeek[]; pendingTimeOff: number; todaySpots?: { time: string; st: string }[] | null; timeOffNews?: { id: number; denied: boolean; text: string }[] }

function tlabel(b: number) {
  const m = 300 + 15 * b; const h = Math.floor(m / 60) % 24; const mm = m % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${mm < 10 ? '0' : ''}${mm}${h >= 12 ? 'p' : 'a'}`;
}

/** storeTab: managers, shift leads and admins on their phone also get the Store tab (managers open on it). */
export function EmployeeApp({ me, onMe, onSignOut, storeTab, onDesktop }: { me: Me; onMe: (m: Me) => void; onSignOut: () => void; storeTab?: boolean; onDesktop?: () => void }) {
  const [tab, setTab] = useState<Tab>(() => (location.pathname.startsWith('/availability') ? 'avail' : location.pathname.startsWith('/week') ? 'week' : location.pathname.startsWith('/time-off') ? 'timeoff' : location.pathname.startsWith('/messages') ? 'messages'
    : storeTab && (location.pathname.startsWith('/store') || (location.pathname === '/' && me.role !== 'lead')) ? 'store' : 'today'));
  const [ver, setVer] = useState(0);
  const [shifts, setShifts] = useState<ShiftsResp | null>(null);
  useEffect(() => { get<ShiftsResp>('/api/me/shifts').then(setShifts).catch(() => setShifts({ today: '', weeks: [], pendingTimeOff: 0 })); }, [tab, ver]);
  const [store, setStore] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [noticeKey, setNoticeKey] = useState(0);
  const [toast, showToast] = useToast();
  const [unread, refreshUnread] = useUnread(true);
  const [msgDraft, setMsgDraft] = useState<{ text: string } | null>(null);

  useEffect(() => {
    get<{ locations: { id: number; name: string }[] }>('/api/locations')
      .then((r) => setStore(r.locations.find((l) => l.id === me.homeLocationId)?.name ?? ''))
      .catch(() => setStore(''));
  }, [me.homeLocationId]);
  const toNews = shifts?.timeOffNews ?? [];
  // the answer to a time-off request has been seen: Dismiss on Today (one), or opening the Time off tab (all)
  const seenTimeOff = (id?: number) => { void post('/api/time-off/mine/seen', id ? { id } : {}).then(() => setVer((v) => v + 1)).catch(() => {}); };
  useEffect(() => { if (tab === 'timeoff' && toNews.length) seenTimeOff(); }, [tab, toNews.length]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [help, setHelp] = useState(false);
  const go = (t: Tab) => { setHelp(false); history.pushState(null, '', t === 'avail' ? '/availability' : t === 'week' ? '/week' : t === 'timeoff' ? '/time-off' : t === 'messages' ? '/messages' : t === 'store' ? '/store' : '/'); setTab(t); setNoticeKey((k) => k + 1); };

  const textsGo = () => {
    if (!me.smsOptIn) { setOptIn(true); return; }
    setConfirm({
      title: 'Stop texts?',
      text: 'Wunderland won’t text you any more: no schedule changes, message notices or sign-in codes. You’ll need to check the app yourself.',
      ok: 'Turn off texts',
      run: async () => {
        try { onMe((await post<{ me: Me }>('/api/auth/texts', { yes: false })).me); }
        catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
      },
    });
  };

  return (
    <div className="approot" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main className="empmain" style={{ display: 'flex', flexDirection: 'column', width: '100%', boxSizing: 'border-box' }}>
        <div className="eappbar">
          <span className="eabrand brand" aria-label="MyWunderland Schedule"><span>My<span className="bw">Wunderland</span></span><span className="bdiv" aria-hidden="true" /><span className="bapp">Schedule</span></span>
          <span className="eastore">{store}</span>
          <button className="elink" onClick={() => setHelp((v) => !v)} aria-pressed={help}>Help</button>
          <button className="elink" onClick={onSignOut}>Sign out</button>
        </div>
        {help && <HelpPage role={me.role} where="phone" hasEmployee={!!me.employeeId} onClose={() => setHelp(false)}
          start={startFor(helpFor(me.role, 'phone', !!me.employeeId), 'phone:' + tab)} />}
        {!help && tab === 'store' && storeTab && <StoreTab me={me} onDesktop={onDesktop} />}
        {!help && tab === 'today' && <Today me={me} textsGo={textsGo} noticeKey={noticeKey} shifts={shifts} store={store} openTimeOff={() => go('timeoff')} unread={unread.mine} openMessages={() => go('messages')} onSeenTimeOff={seenTimeOff} />}
        {!help && tab === 'messages' && <EmployeeMessages key={msgDraft ? 'd' : 'n'} draft={msgDraft} onSeen={refreshUnread} />}
        {!help && tab === 'timeoff' && <TimeOffTab store={store} onChange={() => setVer((v) => v + 1)} />}
        {!help && tab === 'week' && <WeekTab shifts={shifts} store={store} />}
        {!help && tab === 'avail' && <AvailTab noticeKey={noticeKey} showToast={showToast} onMessage={(text) => { setMsgDraft({ text }); go('messages'); }} />}
        <nav className="ebar" aria-label="Sections">
          {([...(storeTab ? [['store', 'Store', '▣']] : []), ['today', 'Today', '◉'], ['week', 'Week', '▦'], ['avail', 'Availability', '◷'], ['timeoff', 'Time off', '☂'], ['messages', 'Messages', '✉']] as [Tab, string, string][]).map(([t, label, icon]) => (
            <button key={t} className={'ebtn' + (tab === t ? ' on' : '')} onClick={() => { if (t !== 'messages') setMsgDraft(null); go(t); }} aria-current={tab === t ? 'page' : 'false'}
              aria-label={t === 'messages' && unread.mine ? `Messages, ${unread.mine} new` : t === 'timeoff' && toNews.length ? `Time off, ${toNews.length} answered` : undefined}>
              <span className="eicon" aria-hidden="true">{icon}</span><span>{label}</span>
              {t === 'messages' && unread.mine > 0 && <span className="ebadge">{unread.mine}</span>}
              {t === 'timeoff' && toNews.length > 0 && tab !== 'timeoff' && <span className="ebadge">{toNews.length}</span>}
            </button>
          ))}
        </nav>
      </main>
      {optIn && <TextsOptIn onClose={() => setOptIn(false)} onDone={(m) => { onMe(m); setOptIn(false); }} />}
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
      {toast}
    </div>
  );
}

/** "Break 2:00p–2:15p", "Lunch 4:00p–4:30p (unpaid)" from a day's row. */
function breakWords(row: string): string[] {
  const out: string[] = [];
  const push = (a: number, b: number) => out.push((row[a] === 'L' ? 'Lunch ' : 'Break ') + tlabel(a) + '–' + tlabel(b) + (row[a] === 'L' ? ' (unpaid)' : ''));
  let a = -1;
  for (let b = 0; b <= row.length; b++) {
    const k = b < row.length ? row[b] : '.';
    if (k === 'B' || k === 'L') {
      if (a >= 0 && row[a] !== k) { push(a, b); a = b; } else if (a < 0) a = b;
    } else if (a >= 0) { push(a, b); a = -1; }
  }
  return out;
}

function Today({ me, textsGo, noticeKey, shifts, store, openTimeOff, unread, openMessages, onSeenTimeOff }: { me: Me; textsGo: () => void; noticeKey: number; shifts: ShiftsResp | null; store: string; openTimeOff: () => void; unread: number; openMessages: () => void; onSeenTimeOff: (id: number) => void }) {
  const all = (shifts?.weeks ?? []).flatMap((w) => w.days);
  const t = all.find((x) => x.date === shifts?.today);
  const next = all.find((x) => x.date > (shifts?.today ?? '') && x.working);
  const now = new Date();
  const date = `${DAY_FULL[now.getDay()]} ${now.getMonth() + 1}/${now.getDate()}`;
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Hi {me.firstName}</h1>
      <div style={{ fontSize: 14, color: '#4A5561' }}>Today · {date}</div>
      {!me.smsOptIn && (
        <div className="txtoff"><span>📵 <b>Text alerts are off.</b> We won’t text you when your schedule changes, when a manager sends you a message, or with sign-in codes, so check the app before your shifts.</span>
          <button className="btn2" onClick={textsGo} style={{ minHeight: 34, padding: '2px 12px', alignSelf: 'flex-start' }}>Turn on texts</button></div>
      )}
      {t?.updated && <span className="updtag" style={{ alignSelf: 'flex-start' }}>Your shift today was changed · {t.updated.replace(/^Updated · /, '')}</span>}
      <div className="chgnote" role="note"><b>Schedules can change.</b> Your shifts, breaks and the stations you’re assigned to may be updated, even on the day. Check the app before each shift.</div>
      <AvailNotice key={noticeKey} />
      {(shifts?.timeOffNews ?? []).map((n) => (
        <div key={n.id} className={'avnotice ' + (n.denied ? 'tonote-denied' : 'tonote-ok')} role="status"><span>{n.text}</span>
          <button className="btn2" onClick={() => onSeenTimeOff(n.id)} style={{ minHeight: 32, padding: '2px 12px', flex: '0 0 auto' }}>Dismiss</button></div>
      ))}
      <CoverCards />
      {!shifts ? null : t?.working ? (
        <div className="tcard">
          <span className="tlabel">Your shift</span>
          <span className="tshift mono">{t.shift}</span>
          <span>at {t.store ?? store} · {t.paidHours} h paid</span>
          {t.left && <span className="tostat denied" style={{ alignSelf: 'flex-start' }}>{t.left}</span>}
          {breakWords(t.blocks).length > 0 && <div className="tsub"><span className="tlabel">Breaks</span>{breakWords(t.blocks).map((x) => <span key={x} className="mono">{x}</span>)}</div>}
          {shifts.todaySpots?.length ? (
            <div className="tsub"><span className="tlabel">Where you’re stationed</span>
              {shifts.todaySpots.map((x) => <span key={x.time}><span className="mono">{x.time}</span> {x.st}</span>)}</div>
          ) : <div className="tsub" style={{ color: '#4A5561', fontSize: 13 }}>Your stations show here once your manager creates today’s rotation.</div>}
        </div>
      ) : t?.left ? (
        <div className="tcard off"><b>{t.left}</b></div>
      ) : t?.timeOff ? (
        <div className="tcard off"><b>{t.timeOff}</b></div>
      ) : next ? (
        <div className="tcard off"><span className="tlabel">You’re off today</span><span>Next shift: <b>{next.label}, {next.shift} at {next.store ?? store}</b></span></div>
      ) : (
        <div className="tcard off"><span className="tlabel">You’re off today</span><span>{(shifts.weeks.length ? 'No more posted shifts yet.' : 'Your shifts will show here once your manager posts the schedule.')}</span></div>
      )}
      {unread > 0 && <button className="enote" onClick={openMessages}>✉ You have {unread} new message{unread === 1 ? '' : 's'} ›</button>}
      {!!shifts?.pendingTimeOff && <button className="enote" onClick={openTimeOff}>☂ {shifts.pendingTimeOff} time-off request{shifts.pendingTimeOff === 1 ? '' : 's'} waiting for a manager ›</button>}
      {me.smsOptIn && <div className="txtquiet">Text alerts are on · <button className="linkbtn" onClick={textsGo}>change</button></div>}
    </section>
  );
}

function TextsOptIn({ onClose, onDone }: { onClose: () => void; onDone: (m: Me) => void }) {
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState('');
  return (
    <Modal title="Get texts from Wunderland?" onClose={onClose}>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: '#17212B' }}>We’ll send automated texts to your mobile number with your schedules and shift updates, message notices and sign-in codes. If you texted STOP before, also text START to our number once.</div>
      <div className="smsterms" role="region" aria-label="Text message program terms" tabIndex={0}>
        {SMS_TERMS.map((x) => <p key={x.h}><b>{x.h}:</b> {x.t}</p>)}
        <p><a href={PRIVACY_URL} target="_blank" rel="noopener">Privacy policy</a> · <a href={TERMS_URL} target="_blank" rel="noopener">Terms &amp; conditions</a></p>
      </div>
      <label className="allday smsagree"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />I have read the terms above and agree to receive text messages from Wunderland at this number.</label>
      {err && <div className="fieldnote bad">{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Not now</button>
        <button className="tbtn primary" disabled={!agree} onClick={async () => {
          try { onDone((await post<{ me: Me }>('/api/auth/texts', { yes: true, agreed: true })).me); }
          catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
        }}>Yes, text me</button>
      </div>
    </Modal>
  );
}

// ---------- Week ----------

function WeekTab({ shifts, store }: { shifts: ShiftsResp | null; store: string }) {
  const [pick, setPick] = useState(0);
  if (!shifts) return null;
  const w = shifts.weeks[Math.min(pick, shifts.weeks.length - 1)];
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Your week</h1>
      {shifts.weeks.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="group" aria-label="Which week">
          {shifts.weeks.map((x, i) => <button key={x.weekStart} className={'pill' + (x === w ? ' on' : '')} onClick={() => setPick(i)}>{x.label}</button>)}
        </div>
      )}
      {!w && <div className="tcard off"><span>Your shifts will show here once your manager posts the schedule.</span></div>}
      {w && (
        <>
          <div style={{ fontSize: 14, color: '#4A5561' }}>Week ending {w.weekEnd} · {w.hours} hours this week</div>
          <div className="chgnote" role="note"><b>Schedules can change.</b> Your shifts, breaks and the stations you’re assigned to may be updated, even on the day. Check the app before each shift.</div>
          <div className="myweek">
            {w.days.map((d) => (
              <div key={d.date} className={d.working ? 'myday on' : 'myday'}>
                <b>{d.label}</b>
                {d.updated && <span className="updtag">{d.updated}</span>}
                <span className="mono">{d.working ? d.shift : 'Off'}</span>
                <span style={{ color: '#4A5561' }}>{d.working ? `at ${d.store ?? store}` : ''}</span>
                <span style={{ color: '#4A5561' }}>{d.working ? `${d.paidHours} h` : ''}</span>
                <span style={{ color: '#4A5561', fontSize: 12 }}>{d.working ? d.breaks.join(' · ') : ''}</span>
                {d.left && <span className="tostat denied">{d.left}</span>}
                {d.timeOff && <span className="tostat approved">{d.timeOff}</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ---------- Availability ----------

interface MyAvail { days: string[]; maxHours: number | null; maxDays: number | null }
interface Edit { wd: number; mode: 'all' | 'hours' | 'none'; from: number; to: number; pref: boolean; pf: number; pt: number }

/** "Available 11:00a–8:00p · prefers 2:00p–6:00p" (9 AM – midnight), as in the prototype. */
function words(row: string): { text: string; cls: string } {
  const runs = (test: (v: string) => boolean) => {
    const out: string[] = []; let a = -1;
    for (let b = 16; b <= 76; b++) { const on = b < 76 && test(row[b]!); if (on && a < 0) a = b; if (!on && a >= 0) { out.push(`${tlabel(a)}–${tlabel(b)}`); a = -1; } }
    return out;
  };
  const av = runs((v) => v !== 'N'); const pf = runs((v) => v === 'P');
  let anyN = false; for (let b = 16; b < 76; b++) if (row[b] === 'N') anyN = true;
  if (!av.length) return { text: 'Not available', cls: 'avday none' };
  return { text: (anyN ? `Available ${av.join(', ')}` : 'Available all day') + (pf.length ? ` · prefers ${pf.join(', ')}` : ''), cls: anyN ? 'avday some' : 'avday all' };
}

function strip(row: string) {
  const out: { cls: string; w: number }[] = []; let a = 16;
  for (let b = 17; b <= 76; b++) if (b === 76 || row[b] !== row[a]) { out.push({ cls: 'avs ' + (row[a] === 'P' ? 'p' : row[a] === 'N' ? 'n' : 'a'), w: b - a }); a = b; }
  return out;
}

function startEdit(row: string, wd: number): Edit {
  let first = -1, last = -1, pf = -1, pt = -1, anyN = false;
  for (let b = 16; b < 76; b++) { if (row[b] === 'N') anyN = true; else { if (first < 0) first = b; last = b + 1; } if (row[b] === 'P') { if (pf < 0) pf = b; pt = b + 1; } }
  const even = (b: number) => b - (b % 2);
  return { wd, mode: first < 0 ? 'none' : anyN ? 'hours' : 'all', from: first < 0 ? 28 : even(first), to: last < 0 ? 64 : even(last + 1),
    pref: pf >= 0, pf: pf < 0 ? 36 : even(pf), pt: pt < 0 ? 52 : even(pt + 1) };
}

function rowFrom(ed: Edit): string {
  const r: string[] = [];
  for (let b = 0; b < 96; b++) {
    let v = ed.mode === 'all' ? '.' : ed.mode === 'none' ? 'N' : b >= ed.from && b < ed.to ? '.' : 'N';
    if (ed.pref && ed.mode !== 'none' && v === '.' && b >= ed.pf && b < ed.pt) v = 'P';
    r.push(v);
  }
  return r.join('');
}

const times = (a: number, z: number) => { const o: number[] = []; for (let b = a; b <= z; b += 2) o.push(b); return o; };

function AvailTab({ noticeKey, showToast, onMessage }: { noticeKey: number; showToast: (m: string) => void; onMessage: (draft: string) => void }) {
  const [d, setD] = useState<MyAvail | null>(null);
  const [ed, setEd] = useState<Edit | null>(null);
  const [warn, setWarn] = useState<{ tick: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { get<MyAvail>('/api/me/availability').then(setD).catch(() => showToast('Can’t load your availability. Try again.')); }, [showToast]);

  const save = async (understood: boolean) => {
    if (!ed) return;
    setBusy(true);
    try {
      const r = await put<{ days: string[] }>('/api/me/availability', { weekday: ed.wd, blocks: rowFrom(ed), understood });
      setD((cur) => cur && { ...cur, days: r.days }); setEd(null); setWarn(null);
    } catch (e) {
      if (e instanceof ApiError && e.data.needsNotice) setWarn({ tick: false });
      else showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.');
    } finally { setBusy(false); }
  };
  const setLimit = async (field: 'maxHours' | 'maxDays', v: string) => {
    try {
      const r = await patch<{ maxHours: number | null; maxDays: number | null }>('/api/me/limits', { [field]: v === '' ? null : Number(v) });
      setD((cur) => cur && { ...cur, maxHours: r.maxHours, maxDays: r.maxDays });
    } catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
  };
  const set = (o: Partial<Edit>) => setEd((cur) => cur && { ...cur, ...o });

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>My availability</h1>
      <AvailNotice key={noticeKey} />
      <div style={{ fontSize: 14, color: '#4A5561' }}>Tap a day to change it. This repeats every week, and your managers see it while they build the schedule. For a one-off day off, use Time off.</div>
      {d && (
        <div className="limitbox">
          <b>Most I want to work each week</b>
          <label>Hours<select className="sel-sm" value={d.maxHours != null ? String(d.maxHours) : ''} onChange={(e) => void setLimit('maxHours', e.target.value)} aria-label="Most hours a week">
            {MAX_H_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label>Days<select className="sel-sm" value={d.maxDays != null ? String(d.maxDays) : ''} onChange={(e) => void setLimit('maxDays', e.target.value)} aria-label="Most days a week">
            {MAX_D_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <span style={{ fontSize: 13, color: '#4A5561' }}>Your managers see this and get a warning if a schedule goes over it.</span>
        </div>
      )}
      {d && (
        <div className="avlist">
          {DAY_FULL.map((day, wd) => {
            const row = d.days[wd]!; const w = words(row); const editing = ed?.wd === wd;
            return (
              <div key={wd} className={w.cls + (editing ? ' editing' : '')}>
                {!editing && (
                  <button className="avrow" onClick={() => setEd(startEdit(row, wd))} aria-label={`Change ${day}`}>
                    <b>{day}</b><span>{w.text}</span>
                    <span className="avstrip" aria-hidden="true">{strip(row).map((x, i) => <span key={i} className={x.cls} style={{ flexGrow: x.w }} />)}</span>
                    <span className="avchev">›</span>
                  </button>
                )}
                {editing && ed && (
                  <div className="avedit">
                    <b>{day}</b>
                    <div className="aechoices" role="group" aria-label="Availability for the day">
                      {([['all', 'All day'], ['hours', 'Only some hours'], ['none', 'Not available']] as [Edit['mode'], string][]).map(([m, label]) => (
                        <button key={m} className={'aechoice' + (ed.mode === m ? ' on' : '')} onClick={() => set({ mode: m })} aria-pressed={ed.mode === m}>{label}</button>
                      ))}
                    </div>
                    {ed.mode === 'hours' && (
                      <div className="aetimes">
                        <label>Available from<select className="sel-sm" value={ed.from} aria-label="Available from"
                          onChange={(e) => { const from = Number(e.target.value); set({ from, to: ed.to <= from ? from + 8 : ed.to }); }}>
                          {times(16, 74).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}</select></label>
                        <label>until<select className="sel-sm" value={ed.to} aria-label="Available until" onChange={(e) => set({ to: Number(e.target.value) })}>
                          {times(ed.from + 2, 76).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}</select></label>
                      </div>
                    )}
                    {ed.mode !== 'none' && (
                      <label className="allday"><input type="checkbox" checked={ed.pref} onChange={(e) => set({ pref: e.target.checked })} />
                        <span><b>Preferred hours:</b> I’d prefer certain hours but understand they are not guaranteed</span></label>
                    )}
                    {ed.pref && ed.mode !== 'none' && (
                      <div className="aetimes">
                        <label>Prefer from<select className="sel-sm" value={ed.pf} aria-label="Prefer from"
                          onChange={(e) => { const pf = Number(e.target.value); set({ pf, pt: ed.pt <= pf ? pf + 4 : ed.pt }); }}>
                          {times(16, 74).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}</select></label>
                        <label>until<select className="sel-sm" value={ed.pt} aria-label="Prefer until" onChange={(e) => set({ pt: Number(e.target.value) })}>
                          {times(ed.pf + 2, 76).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}</select></label>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="ebig" onClick={() => void save(false)} disabled={busy || (ed.mode === 'hours' && ed.to <= ed.from)} style={{ flex: '1 1 auto' }}>Save</button>
                      <button className="btn2" onClick={() => setEd(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {warn && (
        <div className="modalbg" role="presentation"><div className="modal awmodal" role="dialog" aria-modal="true" aria-labelledby="aw-title" style={{ maxWidth: 520 }}>
          <div id="aw-title" style={{ fontSize: 18, fontWeight: 600 }}>Updating Your Availability</div>
          <div className="awtext">
            <p>Your availability helps your manager plan the schedule. When you mark times as unavailable, we will do our best to schedule around them. Please keep in mind that because store needs fluctuate, limiting your overall availability may result in fewer hours being offered to you.</p>
            <p><b>We recommend discussing significant availability changes with your manager first.</b></p>
            <p>Note: If you are limiting your availability as an accommodation for a medical condition, disability, or religious practice, please contact HR directly.</p>
          </div>
          <label className="awtick"><input type="checkbox" checked={warn.tick} onChange={(e) => setWarn({ tick: e.target.checked })} />I understand that my scheduled hours are based on business needs and my availability.</label>
          <div className="awbtns">
            {/* opens a draft to their manager; the change isn't saved (prototype awMsg) */}
            <button className="btn2" onClick={() => { const wd = ed?.wd; setWarn(null); setEd(null);
              onMessage(wd != null ? `Hi, I’d like to talk about changing my availability on ${DAY_FULL[wd]}s. ` : 'Hi, I’d like to talk about changing my availability. '); }}>Message my manager</button>
            <span style={{ flex: '1 1 auto' }} />
            <button className="btn2" onClick={() => setWarn(null)}>Go back</button>
            <button className="tbtn primary" disabled={!warn.tick || busy} onClick={() => void save(true)}>Save preferences</button>
          </div>
        </div></div>
      )}
    </section>
  );
}
